/**
 * Auth Action Handler - Dispatcher único para links de correo de Firebase
 * Firebase envía: /auth/action?mode=<mode>&oobCode=<code>&continueUrl=...
 * Este componente redirige a la página correcta según el modo.
 *
 * Configurar en Firebase Console → Authentication → Templates →
 * "Acción del correo electrónico personalizada" → URL:
 *   https://club-manager-col.web.app/auth/action
 */
import { useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { AlertCircle } from "lucide-react";
import { Button, InlineLoader } from "@/components/ui";

export default function AuthActionPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const mode = searchParams.get("mode");
  const oobCode = searchParams.get("oobCode");
  const continueUrl = searchParams.get("continueUrl");

  useEffect(() => {
    if (!mode || !oobCode) return;

    // Preservar todos los query params al redirigir a la página específica
    const qs = searchParams.toString();

    switch (mode) {
      case "verifyEmail":
      case "verifyAndChangeEmail":
        navigate(`/verify-email?${qs}`, { replace: true });
        break;
      case "resetPassword":
        navigate(`/reset-password?${qs}`, { replace: true });
        break;
      case "recoverEmail":
        // Firebase recupera el email anterior; lo tratamos igual que verify
        navigate(`/verify-email?${qs}`, { replace: true });
        break;
      default:
        // Si viene un continueUrl, redirige ahí como fallback
        if (continueUrl) {
          window.location.href = continueUrl;
        }
    }
  }, [mode, oobCode, continueUrl, navigate, searchParams]);

  // Mientras redirige
  if (mode && oobCode) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 via-white to-fuchsia-50">
        <InlineLoader size="lg" label="Procesando…" />
      </div>
    );
  }

  // Link sin parámetros válidos
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 via-white to-fuchsia-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
        <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <AlertCircle className="w-8 h-8 text-amber-600" />
        </div>
        <h1 className="text-xl font-bold text-gray-900 mb-2">
          Enlace no reconocido
        </h1>
        <p className="text-gray-600 mb-6">
          Este enlace no contiene la información necesaria o está incompleto. Si
          llegaste aquí desde un correo, es posible que haya expirado.
        </p>
        <div className="flex flex-col gap-2">
          <Link to="/forgot-password">
            <Button variant="primary" className="w-full">
              Solicitar nuevo enlace
            </Button>
          </Link>
          <Link to="/login">
            <Button variant="secondary" className="w-full">
              Volver al inicio
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
